<template>
  <div class="form-container">
    <div class="form-header">
      <h2>Edit Member Roles</h2>
    </div>
    <form v-if="member" @submit.prevent="handleUpdateRoles" class="form">
      <div class="details-section">
        <h3>Member Information</h3>
        <div class="details-group">
          <label>Name</label>
          <p class="details-value">{{ member.user_first_name }} {{ member.user_last_name }}</p>
          <label>Email</label>
          <p class="details-value">{{ member.user_email }}</p>
        </div>
      </div>

      <div class="form-section">
        <h3>Roles & Permissions</h3>
        <div 
          class="form-group" 
          v-if="canEditUsers(route.params.organization_id)"
        >
          <label>Organization Roles</label>
          <div class="tag-input-container" @click="focusInput">
            <div class="selected-tags">
              <span v-for="roleName in selectedRoles" :key="roleName" class="tag" :class="roleName.toLowerCase().replace(/\s+/g, '-')">
                {{ roleName }}
                <button
                  type="button"
                  class="remove-tag"
                  @click.stop="removeRole(roleName)"
                  v-if="!isProtectedRole(roleName)"
                >
                  &times;
                </button>
              </span>
            </div>
            <input
              ref="roleInput"
              v-model="searchText"
              type="text"
              class="tag-input"
              placeholder="Type to search roles..."
              @input="onSearchInput"
              @focus="showSuggestions = true"
              @keydown.backspace="handleBackspace"
              @keydown.enter.prevent="handleEnter"
            >
            <div v-if="showSuggestions && filteredRoles.length > 0" class="suggestions-dropdown">
              <div
                v-for="role in filteredRoles"
                :key="role.name"
                class="suggestion-item"
                :class="{ active: role.name === activeRole }"
                @mousedown.prevent="addRole(role.name)"
              >
                {{ role.name }}
              </div>
            </div>
          </div>
        </div>

        <div class="form-group">
          <label>Inherited Permissions</label>
          <div class="list-container">
            <table>
              <thead>
                <tr>
                  <th>Permission</th>
                  <th>Description</th>
                  <th>Inherited From</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="permission in inheritedPermissions" :key="permission.name">
                  <td>
                    <span class="permission-tag">{{ permission.name }}</span>
                  </td>
                  <td>{{ permission.description }}</td>
                  <td>
                    <div class="inherited-roles">
                      <span 
                        v-for="roleName in permission.inheritedFrom" 
                        :key="roleName"
                        class="tag mini"
                        :class="roleName.toLowerCase().replace(/\s+/g, '-')"
                      >
                        {{ roleName }}
                      </span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div class="form-actions">
        <button
          type="button"
          class="delete-button big"
          @click="handleRemoveMember"
          v-if="canRemoveUser(route.params.organization_id)"
        >
          Remove from Organization
        </button>
        <button
          v-if="canEditUsers(route.params.organization_id)"
          type="submit"
          class="submit-button big"
        >
          Update Member Roles
        </button>
        <button type="button" class="cancel-button medium" @click="handleCancel">
          Cancel
        </button>
      </div>
    </form>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import axios from 'axios'
import { isSuperuser } from '@/scripts/permissions'
import { handleApiError } from '@/scripts/shared-utils'
import { canEditUsers, canRemoveUser, isProtectedRole, sortRoles } from '@/scripts/permissions'

const route = useRoute()
const router = useRouter()

const member = ref(null)
const roles = ref([])
const permissions = ref([])
const rolePermissions = ref(new Map())
const selectedRoles = ref([])
const searchText = ref('')
const showSuggestions = ref(false)
const roleInput = ref(null)
const activeRole = ref(null)

const sortedRoles = computed(() => {
  return sortRoles(roles.value)
})

const filteredRoles = computed(() => {
  const search = searchText.value.toLowerCase()
  return sortedRoles.value.filter(role => 
    role.name.toLowerCase().includes(search) && 
    !selectedRoles.value.includes(role.name) &&
    !isProtectedRole(role.name)
  )
})

const inheritedPermissions = computed(() => {
  const allPermissions = new Map()
  
  selectedRoles.value.forEach(roleName => {
    const rolePerms = rolePermissions.value.get(roleName) || new Set()
    rolePerms.forEach(permName => {
      if (!allPermissions.has(permName)) {
        const permission = permissions.value.find(p => p.name === permName)
        if (permission) {
          allPermissions.set(permName, {
            ...permission,
            inheritedFrom: [roleName]
          })
        }
      } else {
        allPermissions.get(permName).inheritedFrom.push(roleName)
      }
    })
  })
  
  return Array.from(allPermissions.values())
})

const focusInput = () => {
  roleInput.value?.focus()
  showSuggestions.value = true
  if (filteredRoles.value.length > 0) {
    activeRole.value = filteredRoles.value[0].name
  }
}

const onSearchInput = () => {
  showSuggestions.value = true
  if (filteredRoles.value.length > 0) {
    activeRole.value = filteredRoles.value[0].name
  }
}

const handleBackspace = () => {
  if (!searchText.value && selectedRoles.value.length > 0) {
    removeRole(selectedRoles.value[selectedRoles.value.length - 1])
  }
}

const handleEnter = () => {
  if (activeRole.value && filteredRoles.value.length > 0) {
    addRole(activeRole.value)
  }
}

const addRole = (roleName) => {
  if (!selectedRoles.value.includes(roleName) && !isProtectedRole(roleName)) {
    selectedRoles.value.push(roleName)
  }
  searchText.value = ''
  showSuggestions.value = false
}

const removeRole = (roleName) => {
  if (!isProtectedRole(roleName)) {
    selectedRoles.value = selectedRoles.value.filter(r => r !== roleName)
  }
}

const goBack = () => {
  if (isSuperuser()) {
    router.push(`/organizations/${route.params.organization_id}/edit`)
  } else {
    router.push(`/organization`)
  }
}

const handleUpdateRoles = async () => {
  if (!selectedRoles.value.length) {
    return
  }
  try {
    await axios.put(
      `/api/organizations/${route.params.organization_id}/members/${route.params.user_id}`,
      { roles: selectedRoles.value }
    )
    goBack()
  } catch (error) {
    handleApiError(error, 'updating member roles')
  }
}

const handleCancel = () => {
  router.back()
}

const handleRemoveMember = async () => {
  if (!confirm('Are you sure you want to remove this member from the organization?')) {
    return
  }

  try {
    await axios.delete(
      `/api/organizations/${route.params.organization_id}/members/${route.params.user_id}`
    )
    goBack()
  } catch (error) {
    handleApiError(error, 'removing member')
  }
}

const fetchMember = async () => {
  try {
    const response = await axios.get(`/api/organizations/${route.params.organization_id}/members`)
    // group the member roles like in OrganizationMembers
    let grouped = null;
    response.data.filter(member => member.user_id === route.params.user_id).forEach(member => {
      if (!grouped) {
        grouped = {
          user_id: member.user_id,
          roles: [],
          organization_id: member.organization_id,
          user_first_name: member.user_first_name,
          user_last_name: member.user_last_name,
          user_email: member.user_email
        };
      }
      if (member.organization_role_id && member.role_name) {
        grouped.roles.push({
          "id": member.organization_role_id,
          "name": member.role_name,
        });
      }
    });
    member.value = grouped
    selectedRoles.value = member.value.roles.map(role => role.name)
  } catch (error) {
    handleApiError(error, 'fetching member')
  }
}

const fetchRoles = async () => {
  try {
    const response = await axios.get(`/api/roles`)
    roles.value = response.data
  } catch (error) {
    handleApiError(error, 'fetching roles')
  }
}

const fetchPermissions = async () => {
  try {
    const response = await axios.get('/api/permissions')
    permissions.value = response.data
  } catch (error) {
    handleApiError(error, 'fetching permissions')
  }
}

const loadRolePermissions = async () => {
  for (const role of roles.value) {
    try {
      const response = await axios.get(`/api/roles/${role.id}/permissions`);
      const permissionSet = new Set(response.data.map(p => p.name));
      rolePermissions.value.set(role.name, permissionSet);
    } catch (error) {
      handleApiError(error, `loading permissions for role ${role.name}`);
    }
  }
}

async function loadData() {
  try {
    await Promise.all([
      fetchMember(),
      fetchRoles(),
      fetchPermissions()
    ]);
    await loadRolePermissions(); // Load role-permission mappings
  } catch (error) {
    handleApiError(error, 'loading data');
  }
}

onMounted(() => {
  loadData();
});
</script>

<style scoped>
@import '@/styles/shared-form.css';
@import '@/styles/shared-list.css';
@import '@/styles/shared-details.css';
@import '@/styles/organization-members.css';
</style>
